import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Text, Tooltip } from "@mantine/core";
import { IconArrowBackUp } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CorrectAnswer,
  SubmitTrackResultResponse,
  TrackResult,
} from "@labeling-tools/api/field-rep/generated/types.gen";
import {
  disputeTask,
  fetchNextTask,
  isTaskUnavailable,
  openTask,
  releaseTask,
  submitTask,
  taskQueryKey,
} from "@/utils/api";
import { TrackFrame } from "@/types/trackLabeling";
import { useProductSet } from "@/components/track-labeling/useProductSet";
import { ProductImagesModal } from "@/components/track-labeling/ProductImagesModal";
import { AssociationFooter } from "@/components/track-labeling/AssociationFooter";
import { CorrectAnswerDialog } from "@/components/track-labeling/CorrectAnswerDialog";
import { LabelerAnswer } from "@/components/track-labeling/correctAnswer";
import {
  DEFAULT_SCROLL_SPEED,
  MISSING_IMAGE_PLACEHOLDER,
  SCROLL_SPEED_OPTIONS,
  SCROLL_SPEED_STEPS,
  SCROLL_SPEED_STORAGE_KEY,
  type ScrollSpeed,
} from "@/config/constants";
import { PredictionGrid } from "@/components/track-labeling/PredictionGrid";
import { ProductGridToolbar } from "@/components/track-labeling/ProductGridToolbar";
import {
  buildAssociationResult,
  buildBadDetectionResult,
  buildProductNotListedResult,
  canConfirmAssociation,
} from "@/components/track-labeling/submission";
import { TrackReviewCard } from "@/components/track-labeling/TrackReviewCard";
import { useTrackKeyboardShortcuts } from "@/components/track-labeling/useTrackKeyboardShortcuts";
import { useTrackReviewState } from "@/components/track-labeling/useTrackReviewState";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLockedAction } from "@/hooks/useLockedAction";
import {
  toFrameProducts,
  toTrackFrames,
} from "@/components/track-labeling/adapters";
import { aggregateFrameProposals } from "@/components/track-labeling/utils";
import { HEADER_HEIGHT } from "@/config/constants";
import { HeaderContent } from "@/components/Header";
import { notifyError, notifySuccess, notifyWarning } from "@/utils/notify";
import {
  productSetIdForNextTask,
  withTaskListFilters,
} from "@/utils/taskListFilters";
import { useTaskListFilters } from "@/hooks/useTaskListFilters";

/** One page instance per task: moving to the next task remounts the page. */
export const TrackLabelingRoute = () => {
  const { taskId } = useParams<{ taskId: string }>();
  return <TrackLabelingPage key={taskId} taskId={taskId ?? ""} />;
};

export const TrackLabelingPage = ({ taskId }: { taskId: string }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Staff sees more information about the task. Labelers only see the essential information
  // so that they won't be able to tell the difference between a normal task vs an accuracy
  // check.
  const { data: currentUser } = useCurrentUser();
  const isStaff = currentUser?.is_staff === true;

  // The selected task list filters.
  const [filters] = useTaskListFilters();
  const selectedProductSetId = filters.productSetId;
  const taskListPath = withTaskListFilters("/", filters);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const taskQuery = useQuery({
    queryKey: taskQueryKey(taskId),
    queryFn: () => openTask(taskId),
    enabled: !!taskId,
    retry: false,
    // Fetched once per visit, never refresh. Because this claims the task, so a background refetch
    // could take back a task staff released, or release the task held in another tab. The signed
    // image URLs last 3 hours. Reloading the page opens the task again with fresh ones.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const task = taskQuery.data ?? null;
  const currentTaskId = task?.id ?? null;

  // For now, a task the labeler can't label (someone else holds it, it's finished or gone,
  // should be very rare) sends them back to the task list. We can consider giving user the
  // option to continue in the future.
  useEffect(() => {
    if (isTaskUnavailable(taskQuery.error)) {
      notifyWarning("This track isn't available", taskQuery.error);
      navigate(taskListPath, { replace: true });
    }
  }, [navigate, taskListPath, taskQuery.error]);

  useEffect(() => {
    if (task?.status === "COMPLETED") {
      notifyWarning(
        "This track is already labeled",
        new Error("Press Start labeling to continue."),
      );
      navigate(taskListPath, { replace: true });
    }
  }, [navigate, taskListPath, task?.status]);

  const frames = useMemo<TrackFrame[]>(
    () => (task ? toTrackFrames(task.frames, MISSING_IMAGE_PLACEHOLDER) : []),
    [task],
  );

  const totalFrames = frames.length;

  // Computed from all frames — clamping in moveToPostConfirmationFrame handles the cut point
  const clearestFramePosition = useMemo(
    () =>
      task
        ? aggregateFrameProposals(task.frames, task.frames.length)
            .clearestFramePosition
        : null,
    [task],
  );

  const {
    cancelChoosingEndFrame,
    clearTransientUiState,
    clampedCurrentFrame,
    effectiveEndFrame,
    handleConfirmTracking,
    handleEndTrack,
    handleFrameSliderChange,
    handleNext,
    handlePrevious,
    hasReachedEnd,
    isChoosingEndFrame,
    isPrimaryConfirmDisabled,
    isTrackingConfirmed,
    jumpTooltipPercentage,
    progressPercentage,
    resetTask,
    safeTotalFrames,
    selectedProduct,
    setSelectedProduct,
    showJumpTooltip,
    startChoosingEndFrame,
    validPercentage,
  } = useTrackReviewState({
    taskId: currentTaskId,
    totalFrames,
    clearestFramePosition,
  });

  // Proposals from the labeled frames only, with each product's details from the task.
  const frameProducts = useMemo(
    () =>
      task
        ? toFrameProducts(task.frames, effectiveEndFrame, task.products)
        : [],
    [task, effectiveEndFrame],
  );

  const [imagesModalProduct, setImagesModalProduct] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const { searchResults, controls: productSetControls } = useProductSet({
    currentTaskId,
    productSetId: task?.job.product_set?.id ?? null,
  });

  // Empty query → recognizer proposals (default). Typed query → server search results.
  const isSearchMode = productSetControls.query.trim().length > 0;
  const displayProducts = useMemo(
    () => (isSearchMode ? searchResults : frameProducts),
    [isSearchMode, searchResults, frameProducts],
  );

  const [scrollSpeed, setScrollSpeed] = useState<ScrollSpeed>(() => {
    if (typeof window === "undefined") return DEFAULT_SCROLL_SPEED;
    const stored = window.localStorage.getItem(SCROLL_SPEED_STORAGE_KEY);
    return SCROLL_SPEED_OPTIONS.includes(stored as ScrollSpeed)
      ? (stored as ScrollSpeed)
      : DEFAULT_SCROLL_SPEED;
  });

  const handleScrollSpeedChange = useCallback((speed: ScrollSpeed) => {
    setScrollSpeed(speed);
    window.localStorage.setItem(SCROLL_SPEED_STORAGE_KEY, speed);
  }, []);

  // Submitting an answer and loading the next task, or releasing the task, run one at a time.
  const { isLocked, run } = useLockedAction();

  const isTrackLoading = taskQuery.isLoading || isLocked;

  const trackError =
    taskQuery.error && !task && !isTrackLoading
      ? taskQuery.error.message
      : null;

  const labeledFrames = useMemo(
    () => task?.frames.slice(0, effectiveEndFrame) ?? [],
    [effectiveEndFrame, task],
  );

  const canConfirmSelectedProduct = canConfirmAssociation({
    selectedProduct,
    frameProducts: displayProducts,
  });

  const moveToNextTask = useCallback(async () => {
    // Skip in case the labler has left the page while the answer is being sent, otherwise
    // we will pull them back into labeling.
    if (!isMountedRef.current) {
      return;
    }

    const productSetId = productSetIdForNextTask(
      selectedProductSetId,
      task?.job.product_set?.id ?? null,
    );

    if (!productSetId) {
      notifyWarning(
        "Pick a product set to keep labeling",
        new Error("This track has no product set."),
      );
      navigate(taskListPath, { replace: true });
      return;
    }

    const nextTaskFilters = { ...filters, productSetId };

    let nextTask;
    try {
      nextTask = await fetchNextTask(productSetId);
    } catch (error) {
      if (isMountedRef.current) {
        notifyError("Couldn't load the next track", error);
        navigate(withTaskListFilters("/", nextTaskFilters), { replace: true });
      }
      return;
    }

    // Same check again after an async call.
    if (!isMountedRef.current) {
      return;
    }

    // No more task left.
    if (!nextTask) {
      notifySuccess(
        "All done",
        "You've labeled every track in this product set. Select another product set.",
      );

      // Back to the list, still on this product set. Replace, so the browser's Back button returns to the
      // task list rather than a finished task.
      navigate(withTaskListFilters("/", nextTaskFilters), { replace: true });
      return;
    }

    queryClient.setQueryData(taskQueryKey(nextTask.id), nextTask);
    navigate(withTaskListFilters(`/tasks/${nextTask.id}`, nextTaskFilters), {
      replace: true,
    });
  }, [
    filters,
    navigate,
    selectedProductSetId,
    queryClient,
    task,
    taskListPath,
  ]);

  // Answer comparison, labeler's against the correct answer, if the task is accuracy check.
  const [answerComparison, setAnswerComparison] = useState<{
    labelerAnswer: LabelerAnswer;
    correctAnswer: CorrectAnswer;
    labeledTrackFrames: TrackFrame[];
    clearestLabeledFramePosition: number;
  } | null>(null);

  const submitTaskResult = useCallback(
    (result: TrackResult, labelerAnswer: LabelerAnswer) => {
      if (!task) {
        return;
      }

      const submittedTaskId = task.id;
      // Captures the labeled frames and the clearest of them now, for the CorrectAnswerDialog.
      const labeledTrackFrames = frames.slice(0, effectiveEndFrame);
      const clearestLabeledFramePosition =
        aggregateFrameProposals(task.frames, effectiveEndFrame)
          .clearestFramePosition ?? 1;

      void run(async () => {
        // Submit current answer and move onto the next.
        let response: SubmitTrackResultResponse | null = null;
        try {
          response = await submitTask(submittedTaskId, result);
        } catch (error) {
          if (!isTaskUnavailable(error)) {
            notifyError("Couldn't save your answer", error);
            return;
          }

          // Generic warning.
          notifyWarning(
            "This track can't take an answer any more. Moving on.",
            error,
          );
        }

        // An incorrect answer to an accuracy check: the labeler sees the correct answer first, and
        // moves on from the dialog.
        const correctAnswer = response?.correct_answer;
        if (
          correctAnswer &&
          labeledTrackFrames.length > 0 &&
          isMountedRef.current
        ) {
          setAnswerComparison({
            labelerAnswer,
            correctAnswer,
            labeledTrackFrames,
            clearestLabeledFramePosition,
          });
          return;
        }

        await moveToNextTask();
      });
    },
    [effectiveEndFrame, frames, moveToNextTask, run, task],
  );

  const handleNextTrack = useCallback(() => {
    void run(moveToNextTask);
  }, [moveToNextTask, run]);

  // Sends the dispute, then moves on to the next track. No catch here: `run` lets errors through,
  // so the dialog's `await` gets them and can show them next to the note, with Try again.
  const handleSendDispute = useCallback(
    async (note: string) => {
      if (!task) {
        return;
      }

      const disputedTaskId = task.id;
      await run(async () => {
        await disputeTask(disputedTaskId, note);
        notifySuccess("Dispute sent", "Staff will review this track.");
        await moveToNextTask();
      });
    },
    [moveToNextTask, run, task],
  );

  const handleRelease = useCallback(() => {
    if (!task) {
      return;
    }

    const releasedTaskId = task.id;

    void run(async () => {
      try {
        await releaseTask(releasedTaskId);
      } catch (error) {
        if (!isTaskUnavailable(error)) {
          notifyError("Couldn't release the track", error);
          return;
        }

        // Generic warning.
        notifyWarning("This track is no longer yours to release", error);
      }

      // The labeler may have left the page while the release was in flight.
      if (isMountedRef.current) {
        navigate(taskListPath, { replace: true });
      }
    });
  }, [navigate, run, task, taskListPath]);

  const handleConfirmAssociation = useCallback(() => {
    // Kept from the products on screen, which may be search results the dialog can't look up later.
    const pickedProduct = displayProducts.find(
      (product) => product.product_id === selectedProduct,
    );
    if (!task?.track_id || !canConfirmSelectedProduct || !pickedProduct) {
      return;
    }

    clearTransientUiState();
    submitTaskResult(
      buildAssociationResult({
        selectedProductId: selectedProduct,
        trackId: task.track_id,
        effectiveEndFrame,
      }),
      { kind: "product", product: pickedProduct },
    );
  }, [
    canConfirmSelectedProduct,
    clearTransientUiState,
    displayProducts,
    effectiveEndFrame,
    selectedProduct,
    submitTaskResult,
    task?.track_id,
  ]);

  const handleProductNotListed = useCallback(() => {
    if (!task?.track_id) {
      return;
    }

    clearTransientUiState();
    submitTaskResult(
      buildProductNotListedResult({
        trackId: task.track_id,
        frames: labeledFrames,
      }),
      { kind: "productNotListed" },
    );
  }, [clearTransientUiState, labeledFrames, submitTaskResult, task?.track_id]);

  const handleBadDetectionSubmit = useCallback(() => {
    if (!task?.track_id) {
      return;
    }

    clearTransientUiState();
    submitTaskResult(buildBadDetectionResult(task.track_id), {
      kind: "badDetection",
    });
  }, [clearTransientUiState, submitTaskResult, task?.track_id]);

  const handlePrimaryConfirm = useCallback(() => {
    handleConfirmTracking();
  }, [handleConfirmTracking]);

  useTrackKeyboardShortcuts({
    canConfirmSelectedProduct,
    fastScrollStep: SCROLL_SPEED_STEPS[scrollSpeed],
    frameProducts: displayProducts,
    hasReachedEnd,
    isChoosingEndFrame,
    isModalOpen: imagesModalProduct !== null || answerComparison !== null,
    isLocked,
    isTrackingConfirmed,
    onBadDetection: handleBadDetectionSubmit,
    onCancelChoosingEndFrame: cancelChoosingEndFrame,
    onConfirmAssociation: handleConfirmAssociation,
    onNext: handleNext,
    onPrevious: handlePrevious,
    onPrimaryConfirm: handlePrimaryConfirm,
    onProductNotListed: handleProductNotListed,
    onSelectProduct: setSelectedProduct,
    selectedProduct,
  });

  return (
    <div
      className="flex flex-col overflow-hidden bg-[#F4F7F9] px-8 py-4 text-left selection:bg-indigo-100 md:px-16"
      style={{ height: `calc(100vh - ${HEADER_HEIGHT}px)` }}
    >
      <div className="mx-auto flex w-full max-w-[1320px] flex-1 min-h-0 flex-col">
        <HeaderContent>
          <Text size="sm" c="dimmed" truncate>
            {isStaff && task?.job.description}
          </Text>
          <Tooltip label="Release this track and return to the task list">
            <Button
              className="ml-auto shrink-0"
              variant="subtle"
              color="gray"
              size="xs"
              leftSection={<IconArrowBackUp size={14} />}
              onClick={handleRelease}
              disabled={!task || isLocked}
            >
              Release task
            </Button>
          </Tooltip>
        </HeaderContent>
        <div className="flex flex-1 min-h-0 flex-col gap-8 overflow-y-auto lg:flex-row lg:overflow-hidden">
          <div className="flex shrink-0 flex-col">
            <TrackReviewCard
              frames={frames}
              clampedCurrentFrame={clampedCurrentFrame}
              totalFrames={totalFrames}
              safeTotalFrames={safeTotalFrames}
              isTrackLoading={isTrackLoading}
              trackError={trackError}
              onPrevious={handlePrevious}
              onNext={handleNext}
              showJumpTooltip={showJumpTooltip}
              jumpTooltipPercentage={jumpTooltipPercentage}
              onFrameSliderChange={handleFrameSliderChange}
              isTrackingConfirmed={isTrackingConfirmed}
              progressPercentage={progressPercentage}
              validPercentage={validPercentage}
              onBadDetection={handleBadDetectionSubmit}
              isChoosingEndFrame={isChoosingEndFrame}
              onStartChoosingEndFrame={startChoosingEndFrame}
              onCancelChoosingEndFrame={cancelChoosingEndFrame}
              onEndTrack={handleEndTrack}
              onPrimaryConfirm={handlePrimaryConfirm}
              onResetTask={resetTask}
              isPrimaryConfirmDisabled={isPrimaryConfirmDisabled}
              isLocked={isLocked}
              trackId={isStaff ? (task?.track_id ?? null) : null}
              scrollSpeed={scrollSpeed}
              onScrollSpeedChange={handleScrollSpeedChange}
            />
          </div>

          {isTrackingConfirmed && (
            <div className="flex min-h-[560px] flex-1 flex-col gap-3 lg:min-h-0">
              <ProductGridToolbar
                hasProducts={displayProducts.length > 0}
                {...productSetControls}
              />
              <PredictionGrid
                products={displayProducts}
                loading={isSearchMode && productSetControls.isSearching}
                selectedProduct={selectedProduct}
                onSelectProduct={setSelectedProduct}
                onConfirmSelection={handleConfirmAssociation}
                onOpenImages={setImagesModalProduct}
                query={productSetControls.query}
              />
              <AssociationFooter
                onConfirmAssociation={handleConfirmAssociation}
                onProductNotListed={handleProductNotListed}
                isConfirmAssociationDisabled={!canConfirmSelectedProduct}
                isLocked={isLocked}
              />
            </div>
          )}
        </div>
      </div>

      <ProductImagesModal
        taskId={currentTaskId}
        product={imagesModalProduct}
        onClose={() => setImagesModalProduct(null)}
      />

      {answerComparison && (
        <CorrectAnswerDialog
          labelerAnswer={answerComparison.labelerAnswer}
          correctAnswer={answerComparison.correctAnswer}
          labeledFrames={answerComparison.labeledTrackFrames}
          clearestLabeledFramePosition={
            answerComparison.clearestLabeledFramePosition
          }
          isLocked={isLocked}
          onNextTrack={handleNextTrack}
          onSendDispute={handleSendDispute}
        />
      )}
    </div>
  );
};
